import { api } from "@/lib/apiClient";

export type ProductLedgerLineKind =
  | "inventory"
  | "supplier_payable"
  | "partner_receivable"
  | "partner_capital"
  | string;

export type ProductLedgerLine = {
  id: number;
  date: string | null;
  voucher: string | null;
  voucherType: string | null;
  lineKind: ProductLedgerLineKind;
  accountTypeCode: string;
  accountTypeName: string;
  detailTypeCode: string;
  detailTypeName: string;
  accountId: number;
  accountName: string;
  accountCode: string;
  costComponent: string;
  particulars: string;
  fromTo: string | null;
  reference: string | null;
  partyId: number | null;
  partyName: string | null;
  debit: number;
  credit: number;
  affectsVehicleCost: boolean;
  vehicleCostBalance: number;
};

export type ProductLedger = {
  product: { id: number; name: string };
  accounts: {
    id: number;
    name: string;
    code: string;
    accountTypeCode: string;
    accountTypeName: string;
    detailTypeCode: string;
    detailTypeName: string;
  }[];
  lines: ProductLedgerLine[];
  totals: {
    inventoryCost: number;
    supplierPayable: number;
    partnerReceivable: number;
    partnerCapital: number;
    vehicleCost: number;
    journalDebit: number;
    journalCredit: number;
    debit: number;
    credit: number;
    difference: number;
  };
};

type ApiProductLedger = {
  product?: { id?: number; name?: string };
  accounts?: {
    id?: number;
    name?: string;
    code?: string;
    account_type_code?: string;
    account_type_name?: string;
    detail_type_code?: string;
    detail_type_name?: string;
  }[];
  lines?: {
    id?: number;
    date?: string | null;
    voucher?: string | null;
    voucher_type?: string | null;
    line_kind?: string;
    account_type_code?: string;
    account_type_name?: string;
    detail_type_code?: string;
    detail_type_name?: string;
    account_id?: number;
    account_name?: string;
    account_code?: string;
    cost_component?: string;
    particulars?: string;
    from_to?: string | null;
    reference?: string | null;
    party_id?: number | null;
    party_name?: string | null;
    debit?: number | string;
    credit?: number | string;
    affects_vehicle_cost?: boolean;
    vehicle_cost_balance?: number | string;
  }[];
  totals?: {
    inventory_cost?: number | string;
    supplier_payable?: number | string;
    partner_receivable?: number | string;
    partner_capital?: number | string;
    vehicle_cost?: number | string;
    journal_debit?: number | string;
    journal_credit?: number | string;
    debit?: number | string;
    credit?: number | string;
    difference?: number | string;
  };
};

export async function fetchProductLedger(productId: string | number): Promise<ProductLedger> {
  const response = await api.get<{ data: ApiProductLedger }>(`/api/v1/catalog/products/${productId}/ledger`);
  const raw = response.data.data ?? {};
  return {
    product: {
      id: Number(raw.product?.id ?? productId),
      name: raw.product?.name ?? "",
    },
    accounts: (raw.accounts ?? []).map((a) => ({
      id: Number(a.id ?? 0),
      name: a.name ?? "",
      code: a.code ?? "",
      accountTypeCode: a.account_type_code ?? "",
      accountTypeName: a.account_type_name ?? "",
      detailTypeCode: a.detail_type_code ?? "",
      detailTypeName: a.detail_type_name ?? "",
    })),
    lines: (raw.lines ?? []).map((line) => ({
      id: Number(line.id ?? 0),
      date: line.date ?? null,
      voucher: line.voucher ?? null,
      voucherType: line.voucher_type ?? null,
      lineKind: (line.line_kind as ProductLedgerLineKind) || "inventory",
      accountTypeCode: line.account_type_code ?? "",
      accountTypeName: line.account_type_name ?? "",
      detailTypeCode: line.detail_type_code ?? "",
      detailTypeName: line.detail_type_name ?? "",
      accountId: Number(line.account_id ?? 0),
      accountName: line.account_name ?? "",
      accountCode: line.account_code ?? "",
      costComponent: line.cost_component || line.account_name || "",
      particulars: line.particulars ?? "",
      fromTo: line.from_to ?? null,
      reference: line.reference ?? null,
      partyId: line.party_id != null ? Number(line.party_id) : null,
      partyName: line.party_name ?? null,
      debit: Number(line.debit ?? 0),
      credit: Number(line.credit ?? 0),
      affectsVehicleCost:
        line.affects_vehicle_cost != null
          ? Boolean(line.affects_vehicle_cost)
          : line.line_kind === "inventory" || !line.line_kind,
      vehicleCostBalance: Number(line.vehicle_cost_balance ?? 0),
    })),
    totals: {
      inventoryCost: Number(raw.totals?.inventory_cost ?? raw.totals?.debit ?? 0),
      supplierPayable: Number(raw.totals?.supplier_payable ?? 0),
      partnerReceivable: Number(raw.totals?.partner_receivable ?? 0),
      partnerCapital: Number(raw.totals?.partner_capital ?? 0),
      vehicleCost: Number(raw.totals?.vehicle_cost ?? raw.totals?.inventory_cost ?? raw.totals?.debit ?? 0),
      journalDebit: Number(raw.totals?.journal_debit ?? 0),
      journalCredit: Number(raw.totals?.journal_credit ?? 0),
      debit: Number(raw.totals?.vehicle_cost ?? raw.totals?.inventory_cost ?? raw.totals?.debit ?? 0),
      credit: Number(raw.totals?.credit ?? 0),
      difference: Number(raw.totals?.difference ?? 0),
    },
  };
}
